"use client";

import { addDaysISO, todayNY } from "@/lib/dates";
import { monthLabel } from "@/lib/i18n";
import { cn } from "@/lib/cn";
import { useMemo, useState } from "react";
import { useLang } from "./lang";

function monthMatrix(year: number, month: number) {
  const first = new Date(year, month, 1);
  const start = (first.getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells: Array<string | null> = [];
  for (let i = 0; i < start; i++) cells.push(null);
  for (let d = 1; d <= days; d++) {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    cells.push(`${year}-${mm}-${dd}`);
  }
  while (cells.length % 7) cells.push(null);
  return cells;
}

export function DateRangeCalendar({
  checkIn,
  checkOut,
  blocked,
  onChange,
}: {
  checkIn: string | null;
  checkOut: string | null;
  blocked: { checkIn: string; checkOut: string }[];
  onChange: (from: string | null, to: string | null) => void;
}) {
  const { t, lang } = useLang();
  const today = todayNY();
  const initial = new Date(`${today}T12:00:00`);
  const [cursor, setCursor] = useState({ y: initial.getFullYear(), m: initial.getMonth() });

  const blockedSet = useMemo(() => {
    const set = new Set<string>();
    for (const r of blocked) {
      let d = r.checkIn;
      while (d < r.checkOut) {
        set.add(d);
        d = addDaysISO(d, 1);
      }
    }
    return set;
  }, [blocked]);

  function pick(day: string) {
    if (day < today || blockedSet.has(day)) return;
    if (!checkIn || (checkIn && checkOut)) {
      onChange(day, null);
      return;
    }
    if (day <= checkIn) {
      onChange(day, null);
      return;
    }
    let d = checkIn;
    while (d < day) {
      if (blockedSet.has(d) && d !== checkIn) {
        onChange(day, null);
        return;
      }
      d = addDaysISO(d, 1);
    }
    onChange(checkIn, day);
  }

  const months = [0, 1].map((offset) => {
    const date = new Date(cursor.y, cursor.m + offset, 1);
    return { y: date.getFullYear(), m: date.getMonth(), label: monthLabel(date.getFullYear(), date.getMonth(), lang) };
  });

  return (
    <div className="card relative z-10 p-4 select-none">
      <div className="flex items-center justify-between mb-3">
        <button
          className="btn btn-ghost min-h-11 min-w-11 px-3 py-1 text-sm touch-manipulation"
          onClick={() => setCursor({ y: cursor.y, m: cursor.m - 1 })}
          type="button"
        >
          {t("prev")}
        </button>
        <p className="text-sm text-muted">{t("nightsLabel")}</p>
        <button
          className="btn btn-ghost min-h-11 min-w-11 px-3 py-1 text-sm touch-manipulation"
          onClick={() => setCursor({ y: cursor.y, m: cursor.m + 1 })}
          type="button"
        >
          {t("next")}
        </button>
      </div>
      {months.map((mo) => (
        <div key={`${mo.y}-${mo.m}`} className="mb-5 last:mb-0">
          <p className="display text-xl mb-2">{mo.label}</p>
          <div className="grid grid-cols-7 text-[11px] text-muted mb-1">
            {t.weekdays.map((d) => (
              <div key={d} className="text-center py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-1">
            {monthMatrix(mo.y, mo.m).map((day, idx) => {
              if (!day) return <div key={idx} />;
              const disabled = day < today || blockedSet.has(day);
              const inRange = Boolean(checkIn && checkOut && day > checkIn && day < checkOut);
              const edge = day === checkIn || day === checkOut;
              return (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => pick(day)}
                  className={cn(
                    "relative z-10 min-h-12 w-full rounded-full text-sm",
                    disabled && "text-muted/40",
                    inRange && "bg-[color-mix(in_srgb,var(--clay)_16%,transparent)] rounded-none",
                    edge && "bg-clay text-white",
                  )}
                >
                  {Number(day.slice(-2))}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
